import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, withRouter } from "react-router";
import { RiLoaderLine, RiAddLine } from "react-icons/ri";
import {
  getAiToolById,
  updateAiToolById,
  uploadAiToolImage,
  getAllAiToolCategories,
} from "@api/seo/aiTool";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Select,
  Upload,
  message,
  Spin,
  Switch,
} from "antd";
import { SuccessNotification, ErrorNotification } from "../..";
import JoditEditor from "jodit-react";

const initialState = {
  name: "",
  image: "",
  description: "",
  content: "",
  category: "",
  inputs: [],
  approved: false,
};

const AiToolUpdateForm = ({ history }) => {
  const { id } = useParams();

  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Reference for the Jodit Editor
  const editorRef = useRef(null);

  useEffect(() => {
    fetchAiTool();
  }, []);

  // Get AI Tool by id
  const fetchAiTool = async () => {
    setLoading(true);
    try {
      const { data } = await getAiToolById(id);

      // Fetch categories
      const cats = await getAllAiToolCategories();
      setCategories(cats.data);

      // Set category
      data.category = data?.category?._id;

      setValues(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("GET_AI_TOOL_BY_ID_ERROR", error);
      ErrorNotification("Error fetching AI Tool");
    }
  };

  // Handle form changes
  const handleFormChange = (e) => {
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });
  };

  // Handle content field change
  const contentFieldChanged = (data) => {
    setValues({
      ...values,
      content: data,
    });
  };

  // Handle form submit
  const handleFormSubmit = (e) => {
    e.preventDefault();

    setUpdateLoading(true);

    // Validate the form
    if (
      !values.name ||
      !values.description ||
      !values.category ||
      !values.content
    ) {
      setUpdateLoading(false);
      return ErrorNotification("Please fill all the required fields");
    }

    updateAiToolById(id, values)
      .then(() => {
        SuccessNotification(`AI Tool updated successfully`);
        setUpdateLoading(false);
        history.goBack();
      })
      .catch((error) => {
        setUpdateLoading(false);
        ErrorNotification("Error updating AI Tool");
        console.log("UPDATE_AI_TOOL_ERROR", error);
      });
  };

  // Before image upload
  const beforeImageUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPEG/PNG files!");
    }
    const isLt2M = file.size / 1024 / 1024 < 3;
    if (!isLt2M) {
      message.error("Image must smaller than 3MB!");
    }
    return isJpgOrPng && isLt2M;
  };

  // Handle image upload
  const handleUploadImage = async (options) => {
    const { onError, file } = options;
    const fmData = new FormData();
    const config = {
      headers: { "content-type": "multipart/form-data" },
    };
    fmData.append("image", file);

    try {
      setLoading(true);
      const res = await uploadAiToolImage(fmData, config);
      setValues({
        ...values,
        image: res.data.url,
      });
      setLoading(false);
    } catch (error) {
      onError(error);
      setLoading(false);
    }
  };

  const uploadButton = (
    <div>
      {loading ? (
        <RiLoaderLine
          className="remix-icon-loading remix-icon-spin remix-icon"
          size={24}
        />
      ) : (
        <RiAddLine size={24} />
      )}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  // Twitter SVG for Jodit Editor
  const twitterSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 432 432">
    <path fill="currentColor" d="M335 159q22-18 28-30q-13 6-31 9q18-13 24-32q-20 11-37 14q-12-14-31-16.5t-35.5 5t-26.5 25t-5 38.5q-67-4-118-59q-11 20-4.5 43.5T120 189q-11-1-24-7q1 43 44 57q-12 3-24 1q12 36 53 40q-15 13-39 19.5T85 303q45 28 92 26q70-3 113.5-49.5T335 159zM384 3q18 0 30.5 12.5T427 45v342q0 17-12.5 29.5T384 429H43q-18 0-30.5-12.5T0 387V45q0-17 12.5-29.5T43 3h341z"/>
  </svg>`;

  // Config for JoditEditor
  const joditConfig = useMemo(
    () => ({
      readonly: false,
      toolbar: true,
      minHeight: 200,
      spellcheck: true,
      language: "en",
      toolbarButtonSize: "medium",
      toolbarAdaptive: false,
      showCharsCounter: true,
      showWordsCounter: true,
      showXPathInStatusbar: false,
      askBeforePasteHTML: true,
      askBeforePasteFromWord: true,
      uploader: {
        insertImageAsBase64URI: true,
      },
      buttons: [
        "source",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "|",
        "font",
        "fontsize",
        "|",
        "ul",
        "ol",
        "|",
        "outdent",
        "indent",
        "|",
        "superscript",
        "subscript",
        "|",
        "brush",
        "paragraph",
        "|",
        "align",
        "left",
        "center",
        "right",
        "justify",
        "|",
        "undo",
        "redo",
        "|",
        "cut",
        "copy",
        "paste",
        "selectall",
        "|",
        "link",
        "unlink",
        "|",
        "image",
        "video",
        {
          name: "Twitter",
          icon: twitterSvg,
          tooltip: "Insert Twitter Embed",
          exec: (editor) => {
            const twitterURL = prompt("Enter Your Twitter Embed Code:");
            if (twitterURL && twitterURL.trim() !== "") {
              const script = document.createElement("script");
              script.src = "https://platform.twitter.com/widgets.js";
              script.async = true;
              document.body.appendChild(script);
              editor.s.insertHTML(
                `<blockquote class="twitter-tweet" style="width: 100%; height: 100%;">${twitterURL}</blockquote>`
              );
              return () => {
                document.body.removeChild(script);
              };
            }
          },
        },
        "|",
        "table",
        "hr",
        "|",
        "symbol",
        "eraser",
        "|",
        "print",
        "fullsize",
        "about",
        "spellcheck",
        "|",
        "preview",
        "print",
        "about",
      ],
    }),
    []
  );

  // Used for embedding Twitter in Jodit
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://platform.twitter.com/widgets.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Handle dynamic inputs
  const handleAddInput = () => {
    setValues({
      ...values,
      inputs: [
        ...values.inputs,
        { label: "", type: "text", options: [], placeholder: "" },
      ],
    });
  };

  const handleInputChange = (index, field, value) => {
    const newInputs = [...values.inputs];
    newInputs[index][field] = value;
    setValues({ ...values, inputs: newInputs });
  };

  const handleRemoveInput = (index) => {
    const newInputs = values.inputs.filter((_, i) => i !== index);
    setValues({ ...values, inputs: newInputs });
  };

  return (
    <Row>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(80vh - 50px)",
            width: "100%",
          }}
        >
          <Spin />
        </div>
      ) : (
        <Col
          span={24}
          style={{ overflowY: "auto", height: "calc(100vh - 100px)" }}
        >
          <Form labelCol={{ span: 4 }} wrapperCol={{ span: 18 }}>
            {/* AI Tool Name */}
            <Form.Item label="Name" required>
              <Input
                name="name"
                placeholder="Enter AI Tool name"
                onChange={handleFormChange}
                value={values.name}
              />
            </Form.Item>

            {/* AI Tool Image */}
            <Form.Item label="Image">
              <Upload
                name="image"
                listType="picture-card"
                customRequest={handleUploadImage}
                className="avatar-uploader"
                showUploadList={false}
                beforeUpload={beforeImageUpload}
                multiple={false}
                accept="image/*"
              >
                {values.image ? (
                  <img
                    src={values.image}
                    alt="avatar"
                    style={{ width: "100%" }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
            </Form.Item>

            {/* SEO Description */}
            <Form.Item label="Description" required>
              <Input.TextArea
                name="description"
                placeholder="Enter SEO description"
                onChange={handleFormChange}
                value={values.description}
                rows={4}
              />
            </Form.Item>

            {/* Category */}
            <Form.Item label="Category" required>
              <Select
                name="category"
                placeholder="Select a category"
                onChange={(value) => setValues({ ...values, category: value })}
                value={values.category}
              >
                {categories.map((category) => (
                  <Select.Option key={category._id} value={category._id}>
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {/* Content */}
            <Form.Item label="Content" required>
              <Col
                style={{
                  borderRadius: "8px",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <JoditEditor
                  ref={editorRef}
                  value={values.content}
                  config={joditConfig}
                  onBlur={(newContent) => contentFieldChanged(newContent)}
                  onChange={() => {}}
                />
              </Col>
            </Form.Item>

            {/* Dynamic Inputs */}
            <Form.Item label="Inputs">
              {values.inputs.map((input, index) => (
                <div key={index} style={{ marginBottom: 16 }}>
                  <Input
                    style={{ width: "30%", marginRight: 8 }}
                    placeholder="Label"
                    value={input.label}
                    onChange={(e) =>
                      handleInputChange(index, "label", e.target.value)
                    }
                  />
                  <Select
                    style={{ width: "20%", marginRight: 8 }}
                    value={input.type}
                    onChange={(value) =>
                      handleInputChange(index, "type", value)
                    }
                  >
                    <Select.Option value="text">Text</Select.Option>
                    <Select.Option value="select">Select</Select.Option>
                    <Select.Option value="number">Number</Select.Option>
                    <Select.Option value="date">Date</Select.Option>
                    <Select.Option value="boolean">Boolean</Select.Option>
                    <Select.Option value="file">File</Select.Option>
                    <Select.Option value="image">Image</Select.Option>
                    <Select.Option value="audio">Audio</Select.Option>
                    <Select.Option value="video">Video</Select.Option>
                    <Select.Option value="url">URL</Select.Option>
                  </Select>
                  {input.type === "select" && (
                    <Input
                      style={{ width: "30%", marginRight: 8 }}
                      placeholder="Options (comma-separated)"
                      value={input.options.join(",")}
                      onChange={(e) =>
                        handleInputChange(
                          index,
                          "options",
                          e.target.value.split(",")
                        )
                      }
                    />
                  )}
                  <Input
                    style={{ width: "20%", marginRight: 8 }}
                    placeholder="Placeholder"
                    value={input.placeholder}
                    onChange={(e) =>
                      handleInputChange(index, "placeholder", e.target.value)
                    }
                  />
                  <Button onClick={() => handleRemoveInput(index)}>
                    Remove
                  </Button>
                </div>
              ))}
              <Button onClick={handleAddInput}>Add Input</Button>
            </Form.Item>

            {/* Approved */}
            <Form.Item label="Approved">
              <Switch
                checked={values.approved}
                onChange={(checked) =>
                  setValues({ ...values, approved: checked })
                }
              />
            </Form.Item>

            {/* Submit button */}
            <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
              <Button
                type="primary"
                onClick={handleFormSubmit}
                loading={updateLoading}
              >
                Update AI Tool
              </Button>
            </Form.Item>
          </Form>
        </Col>
      )}
    </Row>
  );
};

export default withRouter(AiToolUpdateForm);
